import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import ScrollingTable, { percentageRenderer } from '../../Tables/ScrollingTable'
import ExpenseRatio from '../ExpenseRatio'

const TableTitle = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}))

const AnnualizedTotalReturns = ({
  performance,
  name,
  productType,
  tableTitle = '',
  presentation,
  expense_ratio = {},
}) => {
  const columns = useMemo(() => {
    const baseColumns = [
      {
        Header: '',
        accessor: 'fundName',
        Cell: ({ row }) =>
          productType === 'ETF' && row.rowIndex === 0
            ? `${row.fundName} - at NAV`
            : row.fundName,
      },
    ]

    const possibleColumns = [
      { Header: '3 Mo.', accessor: 'quarter' },
      { Header: 'YTD Return', accessor: 'yearToDate' },
      { Header: '1 Yr.', accessor: 'oneYear' },
      { Header: '3 Yr.', accessor: 'threeYear' },
      { Header: '5 Yr.', accessor: 'fiveYear' },
      { Header: '10 Yr.', accessor: 'tenYear' },
      {
        Header: performance.isFundReturn
          ? 'Since Fund Inception'
          : 'Since Inception',
        accessor: 'sinceInception',
      },
    ]

    possibleColumns.forEach((col) => {
      if (performance.fundReturn && performance.fundReturn[col.accessor]) {
        baseColumns.push({ ...col, Cell: percentageRenderer })
      }
    })

    const expenseRatioColumns = ['net', 'gross', 'adjusted']
    expenseRatioColumns.forEach((key) => {
      if (expense_ratio?.[key]) {
        baseColumns.push({
          Header: `${key.charAt(0).toUpperCase() + key.slice(1)} Expense Ratio`,
          accessor: key,
          Cell: ({ value, row }) => (
            <ExpenseRatio ratio={value} popup={row?.[`${key}_popup`]} />
          ),
        })
      }
    })

    return baseColumns
  }, [performance, productType, expense_ratio, name])

  const data = useMemo(() => {
    if (!performance) return []

    const baseData = [
      {
        fundName: name,
        ...performance.fundReturn,
        ...expense_ratio,
      },
    ]

    if (performance.marketReturn?.sinceInception) {
      baseData.push({
        fundName: 'Market Price',
        ...performance.marketReturn,
      })
    }

    performance.indexReturns?.forEach((item) => {
      if (item?.benchmarkIndex?.name) {
        baseData.push({
          fundName: item.benchmarkIndex.name,
          ...item,
        })
      }
    })

    return baseData
  }, [performance, name, expense_ratio])

  const options = useMemo(() => ({
    presentation,
    stripedTable: true,
  }))

  return (
    <>
      {tableTitle && <TableTitle variant="h6">{tableTitle}</TableTitle>}
      <ScrollingTable
        data={data}
        columns={columns}
        options={options}
        otherClassName="table-headers--centered"
      />
    </>
  )
}

AnnualizedTotalReturns.propTypes = {
  performance: PropTypes.shape({
    fundReturn: PropTypes.shape({
      quarter: PropTypes.number,
      yearToDate: PropTypes.number,
      oneYear: PropTypes.number,
      threeYear: PropTypes.number,
      fiveYear: PropTypes.number,
      tenYear: PropTypes.number,
      sinceInception: PropTypes.number,
    }).isRequired,
    indexReturns: PropTypes.array.isRequired,
    marketReturn: PropTypes.shape({
      sinceInception: PropTypes.number,
    }).isRequired,
    calendar: PropTypes.object.isRequired,
  }).isRequired,
  name: PropTypes.string.isRequired,
  productType: PropTypes.string.isRequired,
  tableTitle: PropTypes.string,
  presentation: PropTypes.object,
  expense_ratio: PropTypes.object,
}

export default AnnualizedTotalReturns